import {
  Coords,
  DoneCallback,
  LatLngBounds,
  LeafletMouseEvent,
  Point,
  Rectangle,
  TileLayer,
} from 'leaflet';
import { forkJoin, from, Observable, Subject, Subscription } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { FeatureInfoParamsOptions } from './interfaces/feature-info-params-options';
import { FeatureInfoParamsSettings } from './interfaces/feature-info-params-settings';
import { OverlayLayer } from './interfaces/overlay-layer';
import { Map } from './map';
import 'projects/shared/src/public-api';
import { FeatureInfo, FeatureInfoRootObject } from './interfaces/feature-info';
import { PlatformHits } from './interfaces/platform-hits';
import { PlatformInfo } from './interfaces/platform-info';
import { SharedData } from './core/shared-data';
import { HttpClient } from '@angular/common/http';
import { FeatureInfoPoint } from './interfaces/feature-info-point';
import { PlatformsResponse } from './interfaces/platforms-response';
import { CircleMarker } from './circle-marker';

export class WMSTileLayer extends TileLayer.WMS {
  public httpClient: HttpClient = SharedData.getInstance().httpClient;
  public onMap: boolean = false;
  public wmsLayerOptions?: OverlayLayer;
  public platformHitsOptions?: PlatformHits;
  public platformsInfoSubscription: Subscription | null = null;
  public tilesSubscriptions: Subscription[] = [];
  public loading: Subject<boolean> = new Subject<boolean>();
  public featureInfoLoading: Subject<boolean> = new Subject<boolean>();
  public platformsInfoLoading: Subject<boolean> = new Subject<boolean>();

  public featureInfoChanged: Subject<FeatureInfoPoint> = new Subject<FeatureInfoPoint>();
  public platformsInfoChanged: Subject<PlatformInfo[]> = new Subject<PlatformInfo[]>();
  public platformsId: Subject<string[]> = new Subject<string[]>();

  private controlCql_filter: string = '';
  public currentCqlFilter: string = 'isCruise == false';
  public currentTimeFilter: string = '';
  public wmsCqlFilter: string = '';
  private marker?: CircleMarker;
  private rectangle?: Rectangle;


  constructor(wmsLayerOptions: OverlayLayer, map: Map,) {

    super(wmsLayerOptions.url, wmsLayerOptions);
    this.wmsLayerOptions = wmsLayerOptions;
    const timeout = setTimeout(() => {
      this.loading.next(true);
      clearTimeout(timeout);
    }, 100);

    this.on('loading', () => {
      this.loading.next(true);
    });

    this.on('load', () => {
      this.getPlatformsInfo();
      this.loading.next(false);
    });

    map.on('zoomstart', () => {
      this.clearTilesSubscription();
    });
  }

  private getPlatformsInfo() {
    const baseCql_filter: string = (this.options as any).cql_filter;
    if (this.controlCql_filter === '') {
      this.controlCql_filter = baseCql_filter + '';
    }
    if (this.platformHitsOptions && this.platformHitsOptions.url && this.currentTimeFilter !== '') {

      const obs = this.httpClient
        .get<PlatformsResponse[]>(this.platformHitsOptions.url as string, {
          params: {
            cql_filter: `${this.controlCql_filter}`,
            TIME: this.currentTimeFilter,
            layerTypeName: this.platformHitsOptions.layerTypeName
          } as any,
        })
        .pipe(
          map((m) => {
            return this.platformHitsOptions?.layerFilterCategory?.filters?.map(x => {
              return {
                platformNumber: m.find(f => f.platformtypeCode == x.code)?.numberMatched,
                name: x.categoryName,
                iconUrl: x.iconUrl,
              } as PlatformInfo;
            });
          })
        );

      this.platformsInfoSubscription = obs.subscribe((x) => {
        this.platformsInfoChanged.next(x);
        this.platformsInfoLoading.next(false);

        this.platformsInfoSubscription?.unsubscribe();
        this.platformsInfoSubscription = null;
      });
    }

  }

  private clearTilesSubscription() {
    this.tilesSubscriptions?.forEach(x => x.unsubscribe());
    this.tilesSubscriptions = [];
  }

  updateFilters(filter: string, timeFilter: string = '') {
    if (filter && !filter.includes('object')) {
      this.platformsInfoSubscription?.unsubscribe();
      this.platformsInfoSubscription = null;
      this.clearTilesSubscription();
      this.platformsInfoLoading.next(true);
      let text = this.wmsLayerOptions?.cql_filter
        ? `${this.wmsLayerOptions?.cql_filter} ${filter ? ' AND ' + filter : ''}`
        : filter;

      // if (!text.includes('Vessels')) {
      //   text = text + ' AND isCruise == false';
      // }

      this.currentCqlFilter = text;
      this.currentTimeFilter = timeFilter;
      (this.options as any).cql_filter = text;

      this.setParams({ TIME: timeFilter, cql_filter: text } as any);
    } else {
      this.currentCqlFilter = 'isCruise == false';
      this.setParams({ TIME: timeFilter, cql_filter: this.currentCqlFilter.split(' AND ')[0].trim() } as any);
    }
  }

  setFeatureInfo(
    leafletMap: any,
    event: any,
    settings?: any,
    type?: any
  ) {
    if (type !== 'draw' && type !== 'radar') {
      this.featureInfoLoading.next(true);
      const point = event.containerPoint;
      const layerOptions = this.wmsLayerOptions;
      if ((!layerOptions?.featureInfo && !layerOptions) || !settings) return;
      const size = leafletMap.getSize();
      const options: FeatureInfoParamsOptions = {
        width: size.x,
        height: size.y,
        bbox: settings?.create_bbox(
          settings.wmsVersion,
          leafletMap.getBounds(),
          leafletMap.options.crs
        ),
        service: settings.service,
        version: settings.version,
        layers: layerOptions.layers ?? '',
        format: settings.format,
        transparent: settings.transparent,
        request: settings.request,
        query_layers: layerOptions.layers ?? '',
        X: Math.round(point.x),
        Y: Math.round(point.y),
        INFO_FORMAT: settings.INFO_FORMAT,
        [settings.create_projection_key(settings.wmsVersion)]: leafletMap.options
          ?.crs?.code,
        tiled: settings.tiled,
        feature_count: 100,
        styles: settings.styles ?? '',
        cql_filter: this.currentCqlFilter.replaceAll('%25','%'),
        TIME: this.currentTimeFilter
      };

      const subs = this.httpClient
        .post<FeatureInfoRootObject>(
          layerOptions.url,
          new URLSearchParams(options).toString(),
          { headers: { 'Content-type': 'application/x-www-form-urlencoded' } }
        )
        .pipe(
          map((x) => {
            if (x.features.length > 0) {
              if (this.marker) {
                leafletMap.markerLayerGroup.clearLayers();
                this.marker = new CircleMarker(event.latlng).addTo(leafletMap.markerLayerGroup);
              } else {
                this.marker = new CircleMarker(event.latlng).addTo(leafletMap.markerLayerGroup);
              }
            } else {
              if (this.marker) {
                leafletMap.markerLayerGroup.clearLayers();
              }
            }

            return x.features;
          })
        )
        .subscribe((x: any) => {
            leafletMap.rectangleLayerGroup.clearLayers();
            if (x.length >= 1 && x[0] && x[0].properties && x[0].properties.bbox) {
              const latLng = JSON.parse(x[0].properties.bbox);
              if (latLng) {
                this.rectangle = new Rectangle(latLng).addTo(leafletMap.rectangleLayerGroup);
              }
            }
            this.featureInfoChanged.next({ latLng: event.latlng, featureInfo: x });
            this.featureInfoLoading.next(false);
        });
    } else if (type === 'draw') {

      this.featureInfoLoading.next(true);
      var layer = event.layer;
      var bounds = layer.getBounds();
      const layerOptions = this.wmsLayerOptions;
      if ((!layerOptions?.featureInfo && !layerOptions) || !settings) return;

      const bboxStr = bounds.getSouth() + ',' + bounds.getWest() + ',' +
                      bounds.getNorth() + ',' + bounds.getEast();
      const options: any = {
        service: 'wfs',
        version: '2.0.0',
        typename: layerOptions.layers ?? '',
        request: 'GetFeature',
        outputFormat: 'application/json',
        BBOX: bboxStr,
        srs: 'EPSG:4326'
      };

      const subs = this.httpClient
        .post<any>(
          layerOptions.url,
          new URLSearchParams(options).toString(),
          { headers: { 'Content-type': 'application/x-www-form-urlencoded' } }
        )
        .pipe(
          map((x) => {
            if (x.features.length > 0) {
              let idsArray: string[] = [];
              x.features.forEach((f: any) => {
                idsArray.push(f.id);
              })
              return idsArray;
            } else {
              console.log('errore in tile layer loading');
              return []
            }
          })
        )
        .subscribe((x) => {
          this.platformsId.next(x);
        });
    }
  }

  createTile(coords: Coords, done: DoneCallback): HTMLElement {
    const img = document.createElement('img');
    this.loadTile(coords, done, img);
    return img;
  }

  private loadTile(coords: Coords, done: DoneCallback, img: HTMLImageElement) {
    const [url, urlparams] = this.getTileUrl(coords).split('?');
    const fileReader = new FileReader();

    this.tilesSubscriptions?.push(this.httpClient
      .post<Blob>(url, urlparams, {
        responseType: 'blob' as any,
        headers: { 'Content-type': 'application/x-www-form-urlencoded' },
      })
      .subscribe((x) => {
        fileReader.onload = () => {
          img.src = fileReader.result as string;
          done(undefined, img);
        };
        fileReader.readAsDataURL(x);
      }));
  }

  public openPopupForRadar(radar: any, map: any, latlng: any) {
    this.setFeatureInfo(map, { latLng: latlng }, [radar], 'radar')
  }

}
